/**
 * analytics.js — Cliente de tracking de pageviews.
 *
 * Responsabilidades:
 *  - Generar y persistir un visitor_id estable (UUID v4) en localStorage.
 *  - Generar un session_id por sesión (sessionStorage).
 *  - Capturar parámetros UTM y gclid/fbclid de la URL al primer arribo.
 *  - Enviar pageview al backend en cada navegación.
 *  - Adjuntar header X-Visitor-Id a peticiones a /auth/register y /auth/google/session
 *    para que el backend asocie la fuente de tráfico al usuario creado (first-touch).
 */

const VISITOR_KEY = 'pdd_visitor_id';
const SESSION_KEY = 'pdd_session_id';
const ATTR_KEY = 'pdd_attribution'; // primera fuente que vio el visitor

function uuid() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    // Fallback simple
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
}

export function getVisitorId() {
    try {
        let id = localStorage.getItem(VISITOR_KEY);
        if (!id) {
            id = uuid();
            localStorage.setItem(VISITOR_KEY, id);
        }
        return id;
    } catch {
        return uuid();
    }
}

export function getSessionId() {
    try {
        let id = sessionStorage.getItem(SESSION_KEY);
        if (!id) {
            id = uuid();
            sessionStorage.setItem(SESSION_KEY, id);
        }
        return id;
    } catch {
        return uuid();
    }
}

function readAttribution() {
    try {
        const raw = localStorage.getItem(ATTR_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

function persistAttribution(attr) {
    try {
        if (!attr) return;
        const existing = readAttribution();
        if (existing) return; // first-touch: NO sobreescribimos
        localStorage.setItem(ATTR_KEY, JSON.stringify(attr));
    } catch {
        // ignore
    }
}

function pickQueryParams(search) {
    const params = new URLSearchParams(search || '');
    return {
        utm_source: params.get('utm_source') || '',
        utm_medium: params.get('utm_medium') || '',
        utm_campaign: params.get('utm_campaign') || '',
        utm_content: params.get('utm_content') || '',
        utm_term: params.get('utm_term') || '',
        gclid: params.get('gclid') || '',
        fbclid: params.get('fbclid') || '',
        landing_query: search || '',
    };
}

let _lastPath = null;
let _inflight = false;

/**
 * Envía un pageview al backend. Idempotente por path: si llamas dos veces seguidas
 * para la misma ruta sin cambio, sólo registra una.
 */
export async function trackPageview() {
    const path = window.location.pathname + window.location.search;
    if (_lastPath === path || _inflight) return;
    _inflight = true;

    try {
        const visitor_id = getVisitorId();
        const session_id = getSessionId();
        const utms = pickQueryParams(window.location.search);

        // Persistir first-touch para que el frontend recuerde la atribución
        if (utms.utm_source || utms.gclid || utms.fbclid || document.referrer) {
            persistAttribution({
                utm_source: utms.utm_source,
                utm_campaign: utms.utm_campaign,
                referrer: document.referrer,
                first_seen: new Date().toISOString(),
            });
        }

        const payload = {
            visitor_id,
            session_id,
            path,
            referrer: document.referrer || '',
            ...utms,
            screen_w: window.screen?.width || null,
            screen_h: window.screen?.height || null,
        };

        const url = `${process.env.REACT_APP_BACKEND_URL || ''}/api/track/pageview`;
        await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            keepalive: true,
        });
        _lastPath = path;
    } catch (e) {
        // Tracking nunca debe romper la app
        // eslint-disable-next-line no-console
        console.debug('pageview tracking error (ignored):', e?.message);
    } finally {
        _inflight = false;
    }
}
